import React, { useState } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Chip, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Tooltip, Menu, MenuItem, LinearProgress, Avatar, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, Alert
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  CheckCircle as CompletedIcon,
  Pending as PendingIcon,
  Schedule as InProgressIcon,
  Warning as OverdueIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as BudgetIcon,
  Schedule as TimeIcon,
  Label as TagIcon
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';

const TaskList = ({ tasks, employees, onEditTask, onDeleteTask, filterStatus, filterPriority, filterAssignee, onFilterChange }) => {
  const { translate } = useLanguage();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTimeLogDialog, setShowTimeLogDialog] = useState(false);
  const [timeLog, setTimeLog] = useState({ hours: '', description: '' });

  const handleMenuOpen = (event, task) => {
    setAnchorEl(event.currentTarget);
    setSelectedTask(task);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTask(null);
  };

  const handleStatusChange = (status) => {
    if (selectedTask) {
      const updatedTask = { ...selectedTask, status };
      onEditTask(updatedTask);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (selectedTask) {
      onDeleteTask(selectedTask.id);
    }
    handleMenuClose();
  };

  const handleTimeLog = () => {
    setShowTimeLogDialog(true);
    handleMenuClose();
  };

  const handleSaveTimeLog = () => {
    if (selectedTask && timeLog.hours) {
      const updatedTask = {
        ...selectedTask,
        actualHours: selectedTask.actualHours + parseFloat(timeLog.hours)
      };
      onEditTask(updatedTask);
      setTimeLog({ hours: '', description: '' });
      setShowTimeLogDialog(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'primary';
      case 'pending': return 'warning';
      case 'overdue': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CompletedIcon />;
      case 'in_progress': return <InProgressIcon />;
      case 'pending': return <PendingIcon />;
      case 'overdue': return <OverdueIcon />;
      default: return <PendingIcon />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const isOverdue = (dueDate) => {
    return new Date(dueDate) < new Date() && new Date(dueDate).toDateString() !== new Date().toDateString();
  };

  const getProgressPercentage = (task) => {
    if (task.estimatedHours === 0) return 0;
    return Math.min((task.actualHours / task.estimatedHours) * 100, 100);
  };

  const getBudgetProgress = (task) => {
    if (task.budget === 0) return 0;
    return Math.min((task.spent / task.budget) * 100, 100);
  };

  // Filter tasks based on current filters
  const filteredTasks = tasks.filter(task => {
    if (filterStatus !== 'all' && task.status !== filterStatus) return false;
    if (filterPriority !== 'all' && task.priority !== filterPriority) return false;
    if (filterAssignee !== 'all' && task.assignee !== filterAssignee) return false;
    return true;
  });

  if (filteredTasks.length === 0) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {translate('noTasksFound')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('createFirstTask')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Task Cards View */}
      <Grid container spacing={3}>
        {filteredTasks.map((task) => (
          <Grid item xs={12} md={6} lg={4} key={task.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      {task.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {task.description}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, task)}
                  >
                    <MoreIcon />
                  </IconButton>
                </Box>

                {/* Status and Priority */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Chip
                    icon={getStatusIcon(task.status)}
                    label={translate(task.status)}
                    color={getStatusColor(task.status)}
                    size="small"
                  />
                  <Chip
                    label={translate(task.priority)}
                    color={getPriorityColor(task.priority)}
                    size="small"
                    variant="outlined"
                  />
                  {isOverdue(task.dueDate) && (
                    <Chip
                      label={translate('overdue')}
                      color="error"
                      size="small"
                    />
                  )}
                </Box>

                {/* Assignee */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ width: 24, height: 24, mr: 1 }}>
                    <PersonIcon fontSize="small" />
                  </Avatar>
                  <Typography variant="body2">
                    {task.assignee}
                  </Typography>
                </Box>

                {/* Due Date */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CalendarIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {translate('dueDate')}: {formatDate(task.dueDate)}
                  </Typography>
                </Box>

                {/* Project */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Project: {task.project}
                  </Typography>
                </Box>

                {/* Tags */}
                {task.tags && task.tags.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
                    <TagIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    {task.tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    ))}
                  </Box>
                )}

                {/* Progress */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {translate('progress')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {Math.round(getProgressPercentage(task))}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={getProgressPercentage(task)} 
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>

                {/* Time and Budget */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <TimeIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {translate('estimatedHours')}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {task.estimatedHours}h
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <BudgetIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {translate('budget')}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          ${task.budget}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onEditTask(task)}
                    startIcon={<EditIcon />}
                  >
                    {translate('edit')}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => onDeleteTask(task.id)}
                    startIcon={<DeleteIcon />}
                  >
                    {translate('delete')}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Task Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleStatusChange('pending')}>
          <PendingIcon sx={{ mr: 1 }} />
          {translate('pending')}
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('in_progress')}>
          <InProgressIcon sx={{ mr: 1 }} />
          {translate('inProgress')}
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('completed')}>
          <CompletedIcon sx={{ mr: 1 }} />
          {translate('completed')}
        </MenuItem>
        <MenuItem onClick={handleTimeLog}>
          <TimeIcon sx={{ mr: 1 }} />
          {translate('logTime')}
        </MenuItem>
        <MenuItem onClick={() => onEditTask(selectedTask)}>
          <EditIcon sx={{ mr: 1 }} />
          {translate('editTask')}
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          {translate('deleteTask')}
        </MenuItem>
      </Menu>

      {/* Time Log Dialog */}
      <Dialog open={showTimeLogDialog} onClose={() => setShowTimeLogDialog(false)}>
        <DialogTitle>{translate('logTime')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={translate('hours')}
            type="number"
            value={timeLog.hours}
            onChange={(e) => setTimeLog({ ...timeLog, hours: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            label={translate('description')}
            multiline
            rows={3}
            value={timeLog.description}
            onChange={(e) => setTimeLog({ ...timeLog, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTimeLogDialog(false)}>
            {translate('cancel')}
          </Button>
          <Button onClick={handleSaveTimeLog} variant="contained">
            {translate('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaskList; 